import React, {
  FC,
  ReactNode,
  useContext,
  useMemo,
  useState,
  createContext,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import type { Tokens } from "@okta/okta-auth-js";

import { routes } from "@app/routing";
import {
  ConfigContext,
  NotificationContext,
  ServiceContext,
  UserContext,
} from "@app/provider";
import { LoginStatus } from "@app/provider/user";
import { useOtpState, useTokenSourceFromQuery } from "@app/hooks";
import {
  OTP_STATE_HANDLE_STORAGE_KEY,
  clearOktaAuthStorage,
  createOtpOktaAuth,
  getOtpStateHandle,
  getOtpTiming,
  resolveOtpErrorMessage,
  startOtpIdxFlow,
  verifyOtpCode,
  hasExpiredErrorKey,
  isUserIdNotFoundError,
} from "@app/utils";
import type { OtpTransaction } from "@app/utils";
import { useActivationModeContextOptional } from "@app/provider/activationMode";
import { IDP_TYPE } from "@app/service/user";

export type OtpVerificationMode = "signIn" | "signUp";

export interface OtpVerificationContextValue {
  // State
  otpEmail: string;
  otpTransaction: OtpTransaction | null;
  otpErrorMessage: string | undefined;
  isOtpRequestPending: boolean;
  isOtpVerifyPending: boolean;
  isRedirectingAfterVerify: boolean;
  otpResendKey: number;
  resendTimeoutSeconds: number;
  expiryMinutes: number;
  mode: OtpVerificationMode;

  // Actions
  handleOtpVerify: (code: string) => Promise<void>;
  handleOtpResend: () => Promise<void>;
  handleOtpBack: () => void;
  handleOtpCodeChange: () => void;
  setMode: (mode: OtpVerificationMode) => void;
}

export const OtpVerificationContext = createContext<
  OtpVerificationContextValue | undefined
>(undefined);

export const useOtpVerificationContext = (): OtpVerificationContextValue => {
  const context = useContext(OtpVerificationContext);
  if (!context) {
    throw new Error(
      "useOtpVerificationContext must be used within OtpVerificationProvider"
    );
  }
  return context;
};

interface OtpVerificationProviderProps {
  children: ReactNode;
  /** Initial mode when mounted on Activation page (from ActivationModeContext). */
  initialMode?: OtpVerificationMode;
}

const OtpVerificationProvider: FC<OtpVerificationProviderProps> = ({
  children,
  initialMode,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = useContext(ConfigContext);
  const { clear } = useContext(NotificationContext);
  const { sessionService } = useContext(ServiceContext);
  const { loadProfile, logUserLogin, handleUserLogin } =
    useContext(UserContext);
  const { token } = useTokenSourceFromQuery();

  const [mode, setMode] = useState<OtpVerificationMode>(
    initialMode ?? "signIn"
  );
  const [isRedirectingAfterVerify, setIsRedirectingAfterVerify] =
    useState(false);
  const {
    isOtpRequestPending,
    setIsOtpRequestPending,
    isOtpVerifyPending,
    setIsOtpVerifyPending,
    otpErrorMessage,
    setOtpErrorMessage,
    otpTransaction,
    setOtpTransaction,
    otpResendKey,
    bumpOtpResendKey,
    otpEmail,
    storeOtpStateHandle,
    clearOtpState,
    clearOtpError,
  } = useOtpState();

  const { resendTimeoutSeconds, otpExpiryMinutes } = getOtpTiming(
    config.timeouts?.resendTimeout
  );

  const oktaAuth = useMemo(() => {
    return createOtpOktaAuth(config.okta);
  }, [config.okta]);

  const startOtpFlow = useCallback(
    async (email: string) => {
      setIsOtpRequestPending(true);
      clearOtpError();
      try {
        const { transaction, stateHandle } = await startOtpIdxFlow(
          oktaAuth,
          email
        );
        setOtpTransaction(transaction);
        storeOtpStateHandle(stateHandle);
      } catch (error) {
        const message = error instanceof Error ? error.message : undefined;
        if (!message || isUserIdNotFoundError(message)) {
          return;
        }
        setOtpErrorMessage(resolveOtpErrorMessage(intl));
      } finally {
        setIsOtpRequestPending(false);
      }
    },
    [
      oktaAuth,
      intl,
      location.search,
      navigate,
      clear,
      sessionService,
      clearOtpError,
      setOtpTransaction,
      setOtpErrorMessage,
      setIsOtpRequestPending,
      storeOtpStateHandle,
      clearOtpState,
    ]
  );

  const resetOtpResendState = useCallback(
    async (oktaTokenStorageKey: string) => {
      clearOtpError();
      setOtpTransaction(null);
      sessionStorage.removeItem(OTP_STATE_HANDLE_STORAGE_KEY);
      clearOktaAuthStorage();
      await sessionService.clearToken();
      localStorage.removeItem(oktaTokenStorageKey);
      oktaAuth.tokenManager.clear();
    },
    [clearOtpError, oktaAuth.tokenManager, sessionService, setOtpTransaction]
  );

  const handleOtpResend = async () => {
    if (!otpEmail) return;
    const oktaTokenStorageKey = config.okta.tokenManager.storageKey;
    bumpOtpResendKey();
    await resetOtpResendState(oktaTokenStorageKey);
    await startOtpFlow(otpEmail);
  };

  const handleOtpBack = () => {
    clearOtpState();
    navigate(-1);
  };

  const handleOtpCodeChange = () => {
    if (otpErrorMessage) {
      clearOtpError();
    }
  };

  const handleOtpVerify = async (code: string) => {
    setIsOtpVerifyPending(true);
    clearOtpError();
    try {
      const stateHandle = getOtpStateHandle(otpTransaction);
      if (!stateHandle) {
        throw new Error("Missing OTP state handle");
      }

      const { accessToken, idToken, refreshToken, expiresIn } =
        await verifyOtpCode(oktaAuth, stateHandle, code);

      // Set tokens in token manager - ensure proper format
      const now = Math.floor(Date.now() / 1000);
      const expiresAt = now + expiresIn;

      const tokenData: Tokens = {
        accessToken: {
          accessToken,
          tokenType: "Bearer",
          expiresAt,
        } as Tokens["accessToken"],
      };

      if (idToken) {
        tokenData.idToken = { idToken } as Tokens["idToken"];
      }

      if (refreshToken) {
        tokenData.refreshToken = {
          refreshToken,
        } as Tokens["refreshToken"];
      }

      oktaAuth.tokenManager.setTokens(tokenData);

      const { payload: claims } = oktaAuth.token.decode(accessToken);

      // Save tokens to localStorage in the required format
      if (claims) {
        const issuer =
          typeof claims.iss === "string" ? claims.iss : config.okta.issuer;
        const authorizeUrl = `${issuer}/v1/authorize`;
        const userinfoUrl = `${issuer}/v1/userinfo`;
        const claimScopes =
          Array.isArray(claims.scp) &&
          claims.scp.every((scope) => typeof scope === "string")
            ? (claims.scp as string[])
            : [];
        const scopeList =
          claimScopes.length > 0
            ? claimScopes
            : typeof claims.scope === "string"
              ? claims.scope.split(" ")
              : [];

        const authTokenData = {
          AUTH_TOKEN: {
            accessToken,
            claims,
            expiresAt,
            tokenType: "Bearer",
            scopes: scopeList,
            authorizeUrl,
            userinfoUrl,
          },
        };

        // Save to localStorage with key "okta-token-patient" and value as object with AUTH_TOKEN key
        localStorage.setItem(
          config.okta.tokenManager.storageKey,
          JSON.stringify(authTokenData)
        );
        console.log("Auth token saved to localStorage", authTokenData);
      }

      await logUserLogin({
        email: otpEmail,
        status: LoginStatus.SUCCESS,
        token,
        idpType: IDP_TYPE.OTP,
      });
      await handleUserLogin();
      await loadProfile();
      clearOtpState();

      // Todo. Temporary solution
      // Show loader while waiting before redirect
      setIsRedirectingAfterVerify(true);
      // Wait for state to update and tokens to be available before redirect
      // Use window.location to ensure a full page reload with new auth state
      // Todo. Temporary solution: Find a way to redirect the user to the home page without setTimeout
      setTimeout(() => {
        window.location.href = routes.home;
      }, 2000);
    } catch (error) {
      await logUserLogin({
        email: otpEmail,
        status: LoginStatus.FAIL,
        token,
        idpType: IDP_TYPE.OTP,
      });
      const message = error instanceof Error ? error.message : undefined;
      if (hasExpiredErrorKey(message)) {
        setOtpErrorMessage(resolveOtpErrorMessage(intl, true));
        return;
      }
      setOtpErrorMessage(resolveOtpErrorMessage(intl));
    } finally {
      setIsOtpVerifyPending(false);
    }
  };

  const value = useMemo<OtpVerificationContextValue>(
    () => ({
      otpEmail,
      otpTransaction,
      otpErrorMessage,
      isOtpRequestPending,
      isOtpVerifyPending,
      isRedirectingAfterVerify,
      otpResendKey,
      resendTimeoutSeconds,
      expiryMinutes: otpExpiryMinutes,
      mode,
      handleOtpVerify,
      handleOtpResend,
      handleOtpBack,
      handleOtpCodeChange,
      setMode,
    }),
    [
      otpEmail,
      otpTransaction,
      otpErrorMessage,
      isOtpRequestPending,
      isOtpVerifyPending,
      isRedirectingAfterVerify,
      otpResendKey,
      resendTimeoutSeconds,
      otpExpiryMinutes,
      mode,
      handleOtpVerify,
      handleOtpResend,
      handleOtpBack,
      handleOtpCodeChange,
      setMode,
    ]
  );

  return (
    <OtpVerificationContext.Provider value={value}>
      {children}
    </OtpVerificationContext.Provider>
  );
};

export default OtpVerificationProvider;
